import { useState, useEffect, useRef } from "react";
import {
  CATEGORY_LABELS,
  type EmailTemplate,
  type TemplateCategory,
} from "../../lib/email-templates.ts";
import { saveCustomTemplate } from "../../lib/email-storage.ts";
import { toast } from "../Toast.tsx";

interface TemplateEditorModalProps {
  template: EmailTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (savedTemplate: EmailTemplate) => void;
}

export function TemplateEditorModal({
  template,
  isOpen,
  onClose,
  onSaved,
}: TemplateEditorModalProps) {
  if (!isOpen) return null;

  const [name, setName] = useState(template?.name ?? "Custom Email Template");
  const [category, setCategory] = useState<TemplateCategory>(template?.category ?? "custom");
  const [subject, setSubject] = useState(template?.subject ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [html, setHtml] = useState(template?.html ?? "");
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setCategory(template.category);
      setSubject(template.subject);
      setDescription(template.description);
      setHtml(template.html);
    } else {
      setName("Custom Email Template");
      setCategory("custom");
      setSubject("");
      setDescription("");
      setHtml("");
    }
    setTab("edit");
  }, [template, isOpen]);

  function insertVariable(varTag: string) {
    if (!textareaRef.current) {
      setHtml((prev) => prev + varTag);
      return;
    }
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    setHtml(before + varTag + after);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + varTag.length, start + varTag.length);
    }, 0);
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast("Template name is required.");
      return;
    }
    if (!subject.trim()) {
      toast("Subject line is required.");
      return;
    }
    if (!html.trim()) {
      toast("Email HTML body is required.");
      return;
    }

    try {
      const saved = saveCustomTemplate({
        id: template && !template.isDefault ? template.id : "",
        name: name.trim(),
        category,
        subject: subject.trim(),
        description: description.trim() || `Custom template for ${name.trim()}`,
        html: html.trim(),
      });
      toast("Template saved successfully.", true);
      onSaved(saved);
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to save template");
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal-dialog modal-dialog-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">
            {template && !template.isDefault ? "Edit Custom Template" : "New Custom Template"}
          </div>
          <button type="button" className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <form onSubmit={handleSave} className="modal-body-form">
          <div className="form-grid-2">
            <div className="form-group">
              <label className="label">Template Name</label>
              <input
                type="text"
                className="input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. My Follow-up Template"
              />
            </div>

            <div className="form-group">
              <label className="label">Category</label>
              <select
                className="select"
                value={category}
                onChange={(e) => setCategory(e.target.value as TemplateCategory)}
              >
                {(Object.keys(CATEGORY_LABELS) as TemplateCategory[]).map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABELS[cat]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="label">Subject Line</label>
            <input
              type="text"
              className="input"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Getting started with CurrencyCore / Can I help?"
            />
          </div>

          <div className="form-group">
            <label className="label">Description / Internal Note</label>
            <input
              type="text"
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Sent to users who signed up but have not made requests yet"
            />
          </div>

          <div className="editor-var-strip">
            <span className="text-xs muted">Click to insert dynamic variable:</span>
            <div className="var-chip-list">
              <button
                type="button"
                className="btn btn-xs btn-secondary"
                onClick={() => insertVariable("{{name}}")}
              >
                + {"{{name}}"}
              </button>
              <button
                type="button"
                className="btn btn-xs btn-secondary"
                onClick={() => insertVariable("{{email}}")}
              >
                + {"{{email}}"}
              </button>
              <button
                type="button"
                className="btn btn-xs btn-secondary"
                onClick={() => insertVariable("{{top_plan}}")}
              >
                + {"{{top_plan}}"}
              </button>
              <button
                type="button"
                className="btn btn-xs btn-secondary"
                onClick={() => insertVariable("{{org_count}}")}
              >
                + {"{{org_count}}"}
              </button>
              <button
                type="button"
                className="btn btn-xs btn-secondary"
                onClick={() => insertVariable("{{signup_date}}")}
              >
                + {"{{signup_date}}"}
              </button>
            </div>
          </div>

          <div className="editor-tabs-bar">
            <button
              type="button"
              className={`btn btn-xs ${tab === "edit" ? "btn-secondary" : "btn-ghost"}`}
              onClick={() => setTab("edit")}
            >
              HTML Code Editor
            </button>
            <button
              type="button"
              className={`btn btn-xs ${tab === "preview" ? "btn-secondary" : "btn-ghost"}`}
              onClick={() => setTab("preview")}
            >
              Live Render Preview
            </button>
          </div>

          {tab === "edit" ? (
            <div className="form-group">
              <textarea
                ref={textareaRef}
                className="textarea mono"
                rows={12}
                required
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                placeholder="Enter email HTML markup..."
              />
            </div>
          ) : (
            <div className="modal-preview-box">
              <iframe
                title="Preview"
                srcDoc={html}
                sandbox="allow-popups allow-same-origin"
                className="email-iframe-modal"
              />
            </div>
          )}

          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Template
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
